import type { GridFilterModel, GridPaginationModel, GridSortModel } from '@mui/x-data-grid-pro'
import { createContext, useContext, useRef } from 'react'

import {
  convertFilterModelToSearchParams,
  convertGlobalFiltersToSearchParams,
  convertSearchParamsToFilterModel,
  convertSearchParamsToGlobalFilters,
} from '@/utils/datagrid/filters'
import { convertPaginationModelToSearchParams, convertSearchParamsToPaginationModel } from '@/utils/datagrid/pagination'
import { convertSearchParamsToSortModel, convertSortModelToSearchParams } from '@/utils/datagrid/sorting'
import { syncUrlManagerBuilder } from '@/utils/syncUrlManager'

type DataGridSyncUrlManager = {
  initialState: {
    filterModel: GridFilterModel | null
    paginationModel: GridPaginationModel | null
    sortModel: GridSortModel | null
    globalFilters: Record<string, string> | null
  }
  syncFilterSearchParams: (newFilterModel: GridFilterModel) => void
  syncPaginationSearchParams: (newPaginationModel: Partial<GridPaginationModel>) => void
  syncSortSearchParams: (newSortModel: GridSortModel) => void
  syncGlobalFilterSearchParams: (newGlobalFilters: Record<string, string>) => void
}

type DataGridSyncUrlManagerProviderProps = {
  children: React.ReactNode
}

export const DataGridSyncUrlManagerContext = createContext<DataGridSyncUrlManager>({
  initialState: {
    filterModel: null,
    paginationModel: null,
    sortModel: null,
    globalFilters: null,
  },
  syncFilterSearchParams: () => {},
  syncPaginationSearchParams: () => {},
  syncSortSearchParams: () => {},
  syncGlobalFilterSearchParams: () => {},
})

function dataGridSyncUrlManagerBuilder() {
  const syncUrlManager = syncUrlManagerBuilder()
  const filterModel = convertSearchParamsToFilterModel()
  const paginationModel = convertSearchParamsToPaginationModel()
  const sortModel = convertSearchParamsToSortModel()
  const globalFilters = convertSearchParamsToGlobalFilters()

  let filterParams: Record<string, string> | null = filterModel ? convertFilterModelToSearchParams(filterModel) : null
  let paginationParams: Record<string, string> | null = paginationModel
    ? convertPaginationModelToSearchParams(paginationModel)
    : null
  let sortParams: Record<string, string> | null = sortModel ? convertSortModelToSearchParams(sortModel) : null
  let globalFilterParams: Record<string, string> | null = globalFilters
    ? convertGlobalFiltersToSearchParams(globalFilters)
    : null

  function syncUrl() {
    syncUrlManager.setParams({
      ...filterParams,
      ...paginationParams,
      ...sortParams,
      ...globalFilterParams,
    })

    syncUrlManager.syncUrl()
  }

  return {
    initialState: { filterModel, paginationModel, sortModel, globalFilters },
    syncFilterSearchParams(newFilterModel: GridFilterModel) {
      filterParams = convertFilterModelToSearchParams(newFilterModel)

      syncUrl()
    },

    syncPaginationSearchParams(newPaginationModel: Partial<GridPaginationModel>) {
      paginationParams = convertPaginationModelToSearchParams(newPaginationModel)

      syncUrl()
    },

    syncSortSearchParams(newSortModel: GridSortModel) {
      sortParams = convertSortModelToSearchParams(newSortModel)

      syncUrl()
    },

    syncGlobalFilterSearchParams(newGlobalFilters: Record<string, string>) {
      globalFilterParams = convertGlobalFiltersToSearchParams(newGlobalFilters)

      // Reset pagination params when global filters change
      if (paginationParams) {
        paginationParams = { ...paginationParams, 'pagination.page': '0' }
      }

      syncUrl()
    },
  }
}

/**
 * It provides a context to manage the sync between the changes on the
 * DataGrid and the url. CustomDataGrid component is actually using the hook
 * useDataGridSyncUrlManager to automatically sync the url when pagination, filtering
 * and sorting changes happen.
 *
 * Although it's main use case is to sync the DataGrid with the url, it can be used
 * to sync any component. For example, it can be useful to sync the url after a change
 * on filters from the AppBar content.
 */
export const DataGridSyncUrlManagerProvider = ({ children }: DataGridSyncUrlManagerProviderProps) => {
  const { current: syncUrlManager } = useRef(dataGridSyncUrlManagerBuilder())

  return (
    <DataGridSyncUrlManagerContext.Provider value={syncUrlManager}>{children}</DataGridSyncUrlManagerContext.Provider>
  )
}

export function useDataGridSyncUrlManager() {
  return useContext(DataGridSyncUrlManagerContext)
}
